// Cart drawer / cart page extras: one-click upsells and discount codes.

class CartExtras {
  static parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  // Sections to re-render for the context the element lives in (drawer or cart page).
  static sectionsFor(element) {
    if (element.closest('cart-drawer')) return ['cart-drawer', 'cart-icon-bubble'];
    const ids = [
      document.getElementById('main-cart-items')?.dataset.id,
      document.getElementById('main-cart-footer')?.dataset.id,
      'cart-icon-bubble',
    ];
    return ids.filter(Boolean);
  }

  static async request(url, body, element) {
    const sections = CartExtras.sectionsFor(element);
    const response = await fetch(url, {
      ...fetchConfig(),
      body: JSON.stringify({ ...body, sections, sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message);
    return data;
  }

  static render(sections) {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const source = CartExtras.parse(html);

      if (id === 'cart-drawer') {
        const drawer = document.querySelector('cart-drawer');
        const target = drawer?.querySelector('.drawer__inner');
        const inner = source.querySelector('.drawer__inner');
        if (!target || !inner) return;
        target.innerHTML = inner.innerHTML;
        drawer.classList.toggle('is-empty', Boolean(inner.querySelector('.drawer__inner-empty')));
        return;
      }

      const target = document.getElementById(`shopify-section-${id}`) || document.getElementById(id);
      const sourceSection = source.querySelector('.shopify-section');
      if (target && sourceSection) target.innerHTML = sourceSection.innerHTML;
    });
  }

  static setLoading(button, loading) {
    button.classList.toggle('loading', loading);
    button.setAttribute('aria-disabled', String(loading));
    button.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define(
    'cart-upsell',
    class CartUpsell extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('.cart-upsell__button');
        this.button?.addEventListener('click', this.add.bind(this));
        this.addEventListener('kova:variant-picked', (event) => this.updatePrice(event.detail.variant));
        // Dawn's cart list reads every bubbling "change" as a quantity edit, so the dropdowns are
        // handled here and the event stops at the upsell.
        this.addEventListener('change', (event) => {
          if (!event.target.matches('.gift-variant__option')) return;
          event.stopPropagation();
          const picker = event.target.closest('[data-variant-picker]');
          if (picker) KovaVariantPicker.sync(picker, parseInt(event.target.dataset.optionIndex) || 0);
        });
      }

      updatePrice(variant) {
        if (!variant || variant.price === undefined) return;
        const price = this.querySelector('[data-upsell-price]');
        const compare = this.querySelector('[data-upsell-compare]');
        if (price) price.textContent = Kova.formatMoney(variant.price);
        if (compare) {
          compare.hidden = !(variant.compare > variant.price);
          compare.textContent = compare.hidden ? '' : Kova.formatMoney(variant.compare);
        }
      }

      async add() {
        if (this.button.getAttribute('aria-disabled') === 'true') return;
        const id = this.querySelector('[data-gift-select]')?.value || this.button.dataset.variantId;

        CartExtras.setLoading(this.button, true);
        try {
          const data = await CartExtras.request(`${routes.cart_add_url}.js`, { items: [{ id, quantity: 1 }] }, this);
          CartExtras.render(data.sections);
        } catch (error) {
          console.error(error);
          CartExtras.setLoading(this.button, false);
        }
      }
    }
  );
}

if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('.cart-discount__input');
        this.applyButton = this.querySelector('.cart-discount__apply');

        this.applyButton.addEventListener('click', () => this.apply());
        this.input.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          this.apply();
        });
        this.querySelectorAll('.cart-discount__remove').forEach((button) =>
          button.addEventListener('click', () => this.remove(button.closest('[data-code]').dataset.code))
        );
      }

      get appliedCodes() {
        return Array.from(this.querySelectorAll('[data-code]'), (element) => element.dataset.code);
      }

      async apply() {
        const code = this.input.value.trim();
        if (!code || this.applyButton.getAttribute('aria-disabled') === 'true') return;

        CartExtras.setLoading(this.applyButton, true);
        try {
          let data = await this.update([...new Set([...this.appliedCodes, code])]);
          const entered = data.discount_codes?.find((entry) => entry.code.toLowerCase() === code.toLowerCase());
          const invalid = !entered || !entered.applicable;

          // Shopify keeps codes that don't apply in the cart; drop them so the shopper isn't misled.
          if (invalid) {
            const validCodes = (data.discount_codes || []).filter((entry) => entry.applicable).map((entry) => entry.code);
            data = await this.update(validCodes);
          }

          const context = this.closest('cart-drawer') ? 'cart-drawer' : '#main-cart-footer';
          CartExtras.render(data.sections);
          if (invalid) CartDiscount.showError(context, code);
        } catch (error) {
          console.error(error);
          CartExtras.setLoading(this.applyButton, false);
        }
      }

      async remove(code) {
        CartExtras.setLoading(this.applyButton, true);
        try {
          const data = await this.update(this.appliedCodes.filter((applied) => applied !== code));
          CartExtras.render(data.sections);
        } catch (error) {
          console.error(error);
          CartExtras.setLoading(this.applyButton, false);
        }
      }

      update(codes) {
        return CartExtras.request(`${routes.cart_update_url}.js`, { discount: codes.join(',') }, this);
      }

      // Runs after re-render, so it targets the fresh element that replaced this one.
      static showError(context, code) {
        const element = document.querySelector(context)?.querySelector('cart-discount');
        if (!element) return;
        element.querySelector('.cart-discount__input').value = code;
        element.querySelector('.cart-discount__error').hidden = false;
      }
    }
  );
}

// "Pairs great with" carousel. The drawer and cart page are re-rendered on every cart change,
// so the fetched markup is cached per cart contents to avoid refetching and flicker.
if (!customElements.get('cart-recommendations')) {
  customElements.define(
    'cart-recommendations',
    class CartRecommendations extends HTMLElement {
      static cache = new Map();

      connectedCallback() {
        const key = this.dataset.key;
        if (CartRecommendations.cache.has(key)) {
          this.innerHTML = CartRecommendations.cache.get(key);
          return;
        }

        fetch(this.url)
          .then((response) => response.text())
          .then((text) => {
            const markup = new DOMParser().parseFromString(text, 'text/html').querySelector('.cart-recs')?.outerHTML || '';
            CartRecommendations.cache.set(key, markup);
            if (this.isConnected) this.innerHTML = markup;
          })
          .catch(() => {});
      }

      get url() {
        const { source, productId, recommendationsUrl, rootUrl } = this.dataset;
        if (source === 'related' && productId) {
          return `${recommendationsUrl}?product_id=${productId}&limit=10&intent=related&section_id=cart-recommendations`;
        }
        return `${rootUrl.replace(/\/$/, '')}/?section_id=cart-recommendations`;
      }
    }
  );
}

// Free gifts: after every cart change, a gift line stays only while its product still has the
// gift's minimum quantity in the cart, with one unit per gift slot.
if (!window.kovaGiftSync && typeof subscribe === 'function') {
  window.kovaGiftSync = subscribe(PUB_SUB_EVENTS.cartUpdate, async () => {
    try {
      const cart = await (await fetch(`${routes.cart_url}.js`)).json();
      const quantities = {};
      cart.items.forEach((item) => {
        if (!item.properties?._gift) quantities[item.product_id] = (quantities[item.product_id] || 0) + item.quantity;
      });

      const updates = {};
      const seenSlots = new Set();
      cart.items.forEach((item) => {
        const properties = item.properties || {};
        if (!properties._gift || !properties._gift_for) return;
        const slot = properties._gift_slot || item.key;
        const unlocked = (quantities[properties._gift_for] || 0) >= (parseInt(properties._gift_min) || 1);
        const quantity = unlocked && !seenSlots.has(slot) ? 1 : 0;
        seenSlots.add(slot);
        if (item.quantity !== quantity) updates[item.key] = quantity;
      });
      if (!Object.keys(updates).length) return;

      const context = document.getElementById('main-cart-items') || document.querySelector('cart-drawer');
      if (!context) return;
      const data = await CartExtras.request(`${routes.cart_update_url}.js`, { updates }, context);
      CartExtras.render(data.sections);
    } catch (error) {
      console.error(error);
    }
  });
}
